import {
  boolOptions,
  CUSTOM_PARAM_CATEGORY,
  CUSTOM_PARAMETER_TYPES,
  PERuleFieldDisplayNameMapping,
  PERuleFieldPropertyUnitString,
  PERuleFieldUnitsKey,
  PERuleFieldsChannel,
  PERuleFieldsCustomValue,
  PERuleGroupBaseRate,
  PERuleGroupBorrower,
  PERuleGroupLoan,
  PERuleGroupProduct,
  PERuleGroupProperty,
  PERuleGroupRequest,
} from '@shared/constants';

export const CATEGORY_TO_FIELD_GROUP = {
  [CUSTOM_PARAM_CATEGORY.CUSTOM]: PERuleGroupBaseRate,
  [CUSTOM_PARAM_CATEGORY.BORROWER]: PERuleGroupBorrower,
  [CUSTOM_PARAM_CATEGORY.LOAN]: PERuleGroupLoan,
  [CUSTOM_PARAM_CATEGORY.PROPERTY]: PERuleGroupProperty,
  [CUSTOM_PARAM_CATEGORY.PRODUCT]: PERuleGroupProduct,
};

// PE3 stores these nine Loan-group UI fields under Product. getFieldName encodes
// them on save while parseField decodes them, so both directions read this one
// list to keep them from drifting apart.
const LTV_FIELD_NAMES_STORED_AS_PRODUCT = Object.freeze([
  'LTV',
  'TLTV',
  'CLTV',
  'HCLTV',
  'MaxOfLTVandCLTV',
  'MaxOfTLTVandCLTV',
  'MaxOfTLTVandHCLTV',
  'MaxOfLTVandCLTVandHCLTV',
  'MaxOfTLTVandCLTVandHCLTV',
]);

const LTV_FIELDS_STORED_AS_PRODUCT_REGEX = new RegExp(
  `^${PERuleGroupProduct}\\.(${LTV_FIELD_NAMES_STORED_AS_PRODUCT.join('|')})$`,
  'i',
);

function isLtvFieldPathStoredAsProduct(fieldPath) {
  return LTV_FIELDS_STORED_AS_PRODUCT_REGEX.test(fieldPath);
}

function isLtvFieldNameStoredAsProduct(fieldName) {
  return LTV_FIELD_NAMES_STORED_AS_PRODUCT.some(
    name => name.toUpperCase() === fieldName.toUpperCase(),
  );
}

export class RuleFields {
  constructor(ruleFields, params) {
    this.ruleFields = ruleFields;
    this.params = params || [];
    this.counties = [];
    this.valueGroups = [];
  }

  get fields() {
    return this.ruleFields;
  }

  getCustomParam(fieldName) {
    return (
      this.params.find(param => param.name === fieldName) ?? {
        valueCategory: CUSTOM_PARAM_CATEGORY.CUSTOM,
        valueType: CUSTOM_PARAMETER_TYPES.STRING,
      }
    );
  }

  getCustomField(fieldName) {
    const param = this.getCustomParam(fieldName);
    return this.ruleFields[CATEGORY_TO_FIELD_GROUP[param.valueCategory]][
      fieldName
    ];
  }

  get(group, field) {
    if (group === PERuleFieldsCustomValue && field) {
      return this.getCustomField(field);
    } else if (field && field !== 'null') {
      return this.ruleFields[group][field];
    } else {
      return this.ruleFields[group];
    }
  }

  getFullDisplayName(group, field) {
    if (group === PERuleFieldsCustomValue && field) {
      const param = this.getCustomParam(field);
      group = CATEGORY_TO_FIELD_GROUP[param.valueCategory];
    }

    const displayGroupName = this.getGroupDisplayName(group);
    if (!group && !field) {
      return null;
    } else if (!field || field === 'null') {
      return displayGroupName;
    } else {
      const displayFieldName = this.getFieldDisplayName(group, field);
      return `${displayGroupName} ${displayFieldName}`;
    }
  }

  getGroupDisplayName(group) {
    return PERuleFieldDisplayNameMapping[group]
      ? PERuleFieldDisplayNameMapping[group]
      : group;
  }

  getFieldDisplayName(group, field) {
    let displayFieldName = field;
    if (PERuleFieldDisplayNameMapping[`${group}.${field}`]) {
      displayFieldName = PERuleFieldDisplayNameMapping[`${group}.${field}`];
    }
    return displayFieldName;
  }

  parseField(field) {
    /**
     * Parses rule configuration structure fields from pe3 to js group and field
     */
    if (!field) {
      return {
        entityName: null,
        propertyName: null,
      };
    }

    if (isLtvFieldPathStoredAsProduct(field)) {
      return {
        entityName: PERuleGroupLoan,
        propertyName: field.replaceAll(/^.*(\("|\.)|"\)/g, ''),
      };
    } else if (field.startsWith(PERuleFieldsCustomValue)) {
      return {
        entityName: PERuleFieldsCustomValue,
        propertyName: field.replaceAll(/^.*(\("|\.)|"\)/g, ''),
      };
    } else if (field.startsWith(PERuleFieldsChannel)) {
      return {
        entityName: PERuleGroupBaseRate,
        propertyName: PERuleFieldsChannel,
      };
    } else if (
      Object.keys(this.ruleFields[PERuleGroupRequest]).includes(field)
    ) {
      return {
        entityName: PERuleGroupRequest,
        propertyName: field,
      };
    } else {
      const [entityName, propertyName] = field.split('.');
      return {
        entityName: entityName,
        propertyName: propertyName,
      };
    }
  }

  getFieldName(group, field) {
    /**
     * Generates pe3 rule configuration structure field from js group and field
     */
    if (this.params.some(param => param.name === field)) {
      return `${PERuleFieldsCustomValue}("${field}")`;
    }

    const defaultResult = `${group}.${field}`;
    switch (group) {
      case PERuleGroupLoan:
        return isLtvFieldNameStoredAsProduct(field)
          ? `${PERuleGroupProduct}.${field}`
          : defaultResult;
      case PERuleGroupBaseRate:
        switch (field) {
          case PERuleFieldsChannel:
            return PERuleFieldsChannel;
          default:
            return defaultResult;
        }
      case PERuleGroupProperty:
        if (field === PERuleFieldPropertyUnitString) {
          return `${group}.${PERuleFieldUnitsKey}`;
        } else {
          return defaultResult;
        }
      case PERuleGroupRequest:
        return field;
      default:
        return defaultResult;
    }
  }
}

export function deriveAxisType(ruleFields, group, field) {
  let type = ruleFields.get(group, field);
  if (type === Boolean) type = boolOptions;
  if (group === PERuleFieldsCustomValue && !type) type = String;
  return type;
}
